<div class="dlt-root">
    <!-- Activity chart — dial volume over the range, success vs failed trend lines -->
    <div class="dlt-chart">
        <div class="dlt-chart-head">
            <div class="dlt-chart-heading">
                @if (chartMax > 0) {
                    <span class="dlt-chart-count">{{ chartDialsLabel }}</span>
                    <span class="dlt-chart-sep" aria-hidden="true">·</span>
                    @if (chartFailedCount > 0) {
                        <span class="dlt-chart-stat dlt-chart-stat--fail">{{ chartFailedLabel }}</span>
                    } @else {
                        <span class="dlt-chart-stat dlt-chart-stat--ok">All successful</span>
                    }
                    <span class="dlt-chart-sep" aria-hidden="true">·</span>
                } @else if (isLoading) {
                    <span class="dlt-skel dlt-skel--stat"></span>
                }
                <span class="dlt-chart-range">
                    <span class="pi pi-calendar dlt-chart-range__icon" aria-hidden="true"></span>{{ rangeLabel }}
                </span>
                @if (customRange) {
                    <button
                        type="button"
                        class="dlt-chart-reset"
                        (click)="resetRange()"
                        title="Return to the previously selected range"
                        aria-label="Reset to the previously selected range"
                    >
                        <span class="pi pi-replay" aria-hidden="true"></span>Reset
                    </button>
                }
            </div>
            <div class="dlt-chart-legend">
                <button
                    type="button"
                    class="dlt-chart-key dlt-chart-key--success"
                    [class.dlt-chart-key--off]="!showSuccess"
                    [class.dlt-chart-key--dim]="successDimmed"
                    (click)="toggleSeries('success')"
                >
                    <span class="dlt-chart-dot dlt-chart-dot--success"></span>Success
                </button>
                <button
                    type="button"
                    class="dlt-chart-key dlt-chart-key--failed"
                    [class.dlt-chart-key--off]="!showFailed"
                    [class.dlt-chart-key--dim]="failedDimmed"
                    (click)="toggleSeries('failed')"
                >
                    <span class="dlt-chart-dot dlt-chart-dot--failed"></span>Failed
                </button>
            </div>
        </div>

        <div class="dlt-chart-body" [style.height.px]="chartBodyHeightPx">
            <div class="dlt-chart-yaxis">
                <span class="dlt-chart-ytick dlt-chart-ytick--max">{{ chartMax }}</span>
                @if (chartMax > 1) { <span class="dlt-chart-ytick dlt-chart-ytick--mid">{{ chartMidValue }}</span> }
                <span class="dlt-chart-ytick dlt-chart-ytick--zero">0</span>
            </div>
            <div class="dlt-chart-plot" #dltPlot (mousedown)="onBrushStart($event, dltPlot)">
                <svg
                    class="dlt-chart-svg"
                    [attr.viewBox]="'0 0 ' + CHART_VIEW_W + ' ' + CHART_VIEW_H"
                    preserveAspectRatio="none"
                    aria-hidden="true"
                >
                    <defs>
                        <linearGradient id="dlt-grad-success" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" class="dlt-grad-success-top"></stop>
                            <stop offset="100%" class="dlt-grad-success-bottom"></stop>
                        </linearGradient>
                        <linearGradient id="dlt-grad-failed" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" class="dlt-grad-failed-top"></stop>
                            <stop offset="100%" class="dlt-grad-failed-bottom"></stop>
                        </linearGradient>
                    </defs>
                    <!-- Horizontal gridlines: top (max), mid, baseline (0). -->
                    <line class="dlt-chart-grid" x1="0" [attr.y1]="CHART_VIEW_H * 0.08" [attr.x2]="CHART_VIEW_W" [attr.y2]="CHART_VIEW_H * 0.08" vector-effect="non-scaling-stroke"></line>
                    <line class="dlt-chart-grid" x1="0" [attr.y1]="CHART_VIEW_H * 0.54" [attr.x2]="CHART_VIEW_W" [attr.y2]="CHART_VIEW_H * 0.54" vector-effect="non-scaling-stroke"></line>
                    <!-- Vertical gridlines at the X ticks. -->
                    @for (t of [0, 33.33, 66.66, 100]; track $index) {
                        <line class="dlt-chart-grid dlt-chart-grid--v" [attr.x1]="t / 100 * CHART_VIEW_W" y1="0" [attr.x2]="t / 100 * CHART_VIEW_W" [attr.y2]="CHART_VIEW_H" vector-effect="non-scaling-stroke"></line>
                    }
                    <line class="dlt-chart-baseline" x1="0" [attr.y1]="CHART_VIEW_H" [attr.x2]="CHART_VIEW_W" [attr.y2]="CHART_VIEW_H" vector-effect="non-scaling-stroke"></line>
                    @if (chartMax > 0) {
                        @if (chartSuccessArea) { <path class="dlt-chart-area" [class.dlt-chart-area--dim]="successDimmed" fill="url(#dlt-grad-success)" [attr.d]="chartSuccessArea"></path> }
                        @if (chartFailedArea) { <path class="dlt-chart-area" [class.dlt-chart-area--dim]="failedDimmed" fill="url(#dlt-grad-failed)" [attr.d]="chartFailedArea"></path> }
                        @if (chartSuccessLine) { <path class="dlt-chart-line dlt-chart-line--success" [class.dlt-chart-line--dim]="successDimmed" [attr.d]="chartSuccessLine" vector-effect="non-scaling-stroke"></path> }
                        @if (chartFailedLine) { <path class="dlt-chart-line dlt-chart-line--failed" [class.dlt-chart-line--dim]="failedDimmed" [attr.d]="chartFailedLine" vector-effect="non-scaling-stroke"></path> }
                        @if (hoveredBucket !== null && chartPoints[hoveredBucket]; as pt) {
                            <line class="dlt-chart-crosshair" [attr.x1]="pt.xPct / 100 * CHART_VIEW_W" y1="0" [attr.x2]="pt.xPct / 100 * CHART_VIEW_W" [attr.y2]="CHART_VIEW_H" vector-effect="non-scaling-stroke"></line>
                        }
                        <!-- The cutoff boundary line — starts below the flag (which sits at the top) so the
                             flag isn't crossed by it; the hatched region marks the full-height boundary. -->
                        @if (tableCutoffXPct !== null) {
                            <line class="dlt-chart-cutoff" [attr.x1]="tableCutoffXPct / 100 * CHART_VIEW_W" [attr.y1]="CHART_VIEW_H * 0.18" [attr.x2]="tableCutoffXPct / 100 * CHART_VIEW_W" [attr.y2]="CHART_VIEW_H" vector-effect="non-scaling-stroke"></line>
                        }
                    }
                    <!-- Drag-to-select band: the brushed sub-range, painted while dragging. -->
                    @if (brushing && brushWidthPct > 0) {
                        <rect class="dlt-brush" [attr.x]="brushLeftPct / 100 * CHART_VIEW_W" y="0" [attr.width]="brushWidthPct / 100 * CHART_VIEW_W" [attr.height]="CHART_VIEW_H"></rect>
                        <line class="dlt-brush-edge" [attr.x1]="brushLeftPct / 100 * CHART_VIEW_W" [attr.x2]="brushLeftPct / 100 * CHART_VIEW_W" y1="0" [attr.y2]="CHART_VIEW_H" vector-effect="non-scaling-stroke"></line>
                        <line class="dlt-brush-edge" [attr.x1]="(brushLeftPct + brushWidthPct) / 100 * CHART_VIEW_W" [attr.x2]="(brushLeftPct + brushWidthPct) / 100 * CHART_VIEW_W" y1="0" [attr.y2]="CHART_VIEW_H" vector-effect="non-scaling-stroke"></line>
                    }
                </svg>

                @if (chartMax > 0) {
                    <!-- The region the table can't reach (older than its oldest listed dial): hatched so
                         it reads as "chart-only". HTML overlay so the diagonal hatch isn't stretched. -->
                    @if (tableCutoffXPct !== null) {
                        <div class="dlt-chart-cutoff-region" [style.width.%]="tableCutoffXPct" aria-hidden="true"></div>
                    }
                    <!-- Flag at the table's coverage cutoff — a table icon, positioned on the marker line. -->
                    @if (tableCutoffXPct !== null) {
                        <span
                            class="dlt-chart-cutoff-flag"
                            [class.dlt-chart-cutoff-flag--right]="tableCutoffXPct > 70"
                            [style.left.%]="tableCutoffXPct"
                            [matTooltip]="tableCutoffTooltip"
                            matTooltipPosition="above"
                            tabindex="0"
                            role="img"
                            aria-label="Table coverage limit"
                        >
                            <span class="pi pi-list" aria-hidden="true"></span>Table limit
                        </span>
                    }
                    <!-- "Now" end-dots at the last point of each visible line. -->
                    @if (chartPoints.length && chartPoints[chartPoints.length - 1]; as last) {
                        @if (showSuccess && chartSuccessLine) { <span class="dlt-chart-end dlt-chart-end--success" [class.dlt-chart-end--dim]="successDimmed" [style.top.%]="last.successYPct"></span> }
                        @if (showFailed && chartFailedLine) { <span class="dlt-chart-end dlt-chart-end--failed" [class.dlt-chart-end--dim]="failedDimmed" [style.top.%]="last.failedYPct"></span> }
                    }
                    <!-- Focus dots for the hovered bucket (HTML overlay so they aren't stretched). -->
                    @if (hoveredBucket !== null && chartPoints[hoveredBucket]; as pt) {
                        @if (showSuccess) { <span class="dlt-chart-focus dlt-chart-focus--success" [style.left.%]="pt.xPct" [style.top.%]="pt.successYPct"></span> }
                        @if (showFailed) { <span class="dlt-chart-focus dlt-chart-focus--failed" [style.left.%]="pt.xPct" [style.top.%]="pt.failedYPct"></span> }
                    }
                    <!-- Hover hit-zones: one full-height slot per bucket, driving crosshair + popover. -->
                    <div class="dlt-chart-hovers">
                        @for (pt of chartPoints; track $index) {
                            <div
                                class="dlt-chart-hover"
                                [style.left.%]="pt.xPct - pt.widthPct / 2"
                                [style.width.%]="pt.widthPct"
                                (mouseenter)="setHoveredBucket($index)"
                                (mouseleave)="setHoveredBucket(null)"
                            ></div>
                        }
                    </div>
                    <!-- Custom popover for the hovered bucket. -->
                    @if (hoveredPoint; as pt) {
                        <div
                            class="dlt-chart-tip nf-charttip"
                            [class.dlt-chart-tip--left]="pt.xPct < 20"
                            [class.dlt-chart-tip--right]="pt.xPct > 80"
                            [style.left.%]="pt.xPct"
                        >
                            <div class="nf-charttip__time">{{ pt.windowLabel }}</div>
                            <div class="nf-charttip__rows">
                                @if (showSuccess) {
                                    <div class="nf-charttip__row">
                                        <span class="nf-charttip__label"><span class="nf-charttip__swatch dlt-tip-swatch--success"></span>Success</span>
                                        <span class="nf-charttip__val">{{ pt.success }}</span>
                                    </div>
                                }
                                @if (showFailed) {
                                    <div class="nf-charttip__row">
                                        <span class="nf-charttip__label"><span class="nf-charttip__swatch dlt-tip-swatch--failed"></span>Failed</span>
                                        <span class="nf-charttip__val">{{ pt.failed }}</span>
                                    </div>
                                }
                            </div>
                        </div>
                    }
                } @else if (isLoading) {
                    <div class="dlt-chart-skeleton" aria-hidden="true"></div>
                } @else {
                    <span class="dlt-chart-empty">No activity in this range</span>
                }
                <!-- Floating label of the span being brushed. -->
                @if (brushing && brushWidthPct > 0) {
                    <div class="dlt-brush-tip" [style.left.%]="brushTipLeftPct">{{ brushLabel }}</div>
                }
            </div>
        </div>

        @if (chartMax > 0) {
            <div class="dlt-chart-xaxis">
                @for (tick of chartXTicks; track $index) {
                    <span class="dlt-chart-xtick">{{ tick }}</span>
                }
            </div>
        }
    </div>

    <!-- Coverage footer: the table is capped at FETCH_SIZE, so read its loaded share of the
         total (the headline count) and let the user page the remainder into the table. -->
    @if (showCoverage) {
        <div class="dlt-coverage">
            @if (isLoading) {
                <!-- Placeholder while the table fetch is in flight — the chart aggregation lands
                     first, so without this the done/meter state would flash before rows arrive. -->
                <span class="dlt-skel dlt-skel--cover-bar"></span>
                <span class="dlt-skel dlt-skel--cover-text"></span>
                <span class="dlt-skel dlt-skel--cover-btn"></span>
            } @else if (hasMoreDials) {
                <span class="dlt-coverage-track">
                    <span class="dlt-coverage-fill" [style.width.%]="coveragePct"></span>
                </span>
                <span class="dlt-coverage-count">
                    <span class="dlt-coverage-loaded">{{ records.length | number }}</span> of
                    {{ chartTotalDials | number }} loaded
                </span>
                <span class="dlt-coverage-sep" aria-hidden="true">·</span>
                <button type="button" class="dlt-coverage-btn" [disabled]="loadingMore" (click)="loadOlder()">
                    @if (loadingMore) {
                        <span class="pi pi-spinner pi-spin" aria-hidden="true"></span>Loading dials…
                    } @else {
                        <span class="pi pi-history" aria-hidden="true"></span>{{ loadMoreLabel }}
                    }
                </button>
            } @else {
                <span class="dlt-coverage-done">
                    <span class="pi pi-check-circle" aria-hidden="true"></span>All
                    {{ chartTotalDials | number }} dials loaded
                </span>
            }
        </div>
    }

    <!-- Divider + drag handle: scales the plot height (persisted). Doubles as the chart/toolbar rule. -->
    <div
        class="dlt-chart-resize"
        [class.dlt-chart-resize--active]="resizingChart"
        (mousedown)="startChartResize($event)"
        title="Drag to resize the chart"
        role="separator"
        aria-orientation="horizontal"
        aria-label="Resize chart height"
    >
        <span class="dlt-chart-resize-grip" aria-hidden="true"></span>
    </div>

    <!-- Toolbar: labeled toggles (row 1) + search (row 2) -->
    <div class="dlt-toolbar">
        <div class="dlt-toolbar-controls">
            <div class="dlt-control dlt-control--range">
                <span class="dlt-control-label">Time Range</span>
                <div class="dlt-seg-group">
                    @for (r of auditRanges; track r.id) {
                        <button
                            type="button"
                            class="dlt-seg-btn"
                            [class.dlt-seg-btn--active]="!customRange && selectedRange === r.id"
                            (click)="setRange(r.id)"
                        >
                            {{ r.label }}
                        </button>
                    }
                    <!-- Opens the calendar picker; also lights up when a range is brushed on the chart. -->
                    <button
                        type="button"
                        class="dlt-seg-btn dlt-seg-btn--custom"
                        [class.dlt-seg-btn--active]="customRange"
                        [class.dlt-seg-btn--open]="showCustom"
                        (click)="toggleCustom()"
                        title="Pick a custom range (or drag across the chart)"
                    >
                        Custom
                    </button>
                </div>
                @if (showCustom) {
                    <div class="dlt-daterange__scrim" (click)="showCustom = false" aria-hidden="true"></div>
                    <div class="dlt-daterange__pop">
                        <app-date-range-calendar
                            [start]="newStartTime.valueOf()"
                            [end]="newEndTime.valueOf()"
                            [maxDays]="30"
                            (apply)="onCustomApply($event)"
                            (cancel)="showCustom = false"
                        ></app-date-range-calendar>
                    </div>
                }
            </div>

            @if (isEndpointView) {
                <div class="dlt-control">
                    <span class="dlt-control-label">Direction</span>
                    <div class="dlt-seg-group" role="group" aria-label="Direction">
                        @for (d of directionFilters; track d.id) {
                            <button
                                type="button"
                                class="dlt-seg-btn dlt-seg-btn--dir"
                                [class.dlt-seg-btn--active]="directionFilter === d.id"
                                [matTooltip]="d.id === 'dialed' ? 'Services this connector dials' : 'Services this connector hosts / binds'"
                                (click)="setDirection(d.id)"
                            >
                                @if (d.id === 'dialed') {
                                    <span class="pi pi-arrow-up-right" aria-hidden="true"></span>
                                } @else {
                                    <span class="pi pi-server" aria-hidden="true"></span>
                                }
                                <span>{{ d.label }}</span>
                            </button>
                        }
                    </div>
                </div>
            }

            <div class="dlt-control">
                <span class="dlt-control-label">Result</span>
                <div class="dlt-seg-group">
                    @for (s of statusFilters; track s.id) {
                        <button
                            type="button"
                            class="dlt-seg-btn"
                            [class.dlt-seg-btn--active]="statusFilter === s.id"
                            [class.dlt-seg-btn--success]="statusFilter === s.id && s.id === 'success'"
                            [class.dlt-seg-btn--failed]="statusFilter === s.id && s.id === 'failed'"
                            (click)="setStatusFilter(s.id)"
                        >
                            @switch (s.id) {
                                @case ('success') { <span class="pi pi-check" aria-hidden="true"></span> }
                                @case ('failed') { <span class="pi pi-times" aria-hidden="true"></span> }
                                @default { <span class="pi pi-list" aria-hidden="true"></span> }
                            }
                            <span>{{ s.label }}</span>
                        </button>
                    }
                </div>
            </div>

            <div class="dlt-toolbar-actions">
                <button
                    type="button"
                    class="dlt-icon-btn"
                    [disabled]="isLoading"
                    [matTooltip]="isLoading ? 'Refreshing…' : 'Refresh dial logs'"
                    (click)="refresh()"
                >
                    <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true">
                        <path d="M13.5 8a5.5 5.5 0 1 1-1.6-3.9"></path>
                        <polyline points="13.7 2.5 13.7 5 11.2 5"></polyline>
                    </svg>
                </button>

                <div class="dlt-autorefresh" (click)="$event.stopPropagation()">
                    <button
                        type="button"
                        class="dlt-autorefresh-btn"
                        [class.dlt-autorefresh-btn--on]="autoRefreshSeconds > 0"
                        [class.dlt-autorefresh-btn--open]="autoRefreshMenuOpen"
                        [attr.aria-label]="'Auto-refresh: ' + autoRefreshLabel"
                        matTooltip="Auto-refresh"
                        matTooltipShowDelay="300"
                        (click)="toggleAutoRefreshMenu($event)"
                    >
                        <svg class="dlt-autorefresh-icon" viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                            <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"></path>
                            <path d="M3 3v5h5"></path>
                            <path d="M12 7v5l4 2"></path>
                        </svg>
                        <span class="dlt-ar-label">Auto-refresh:</span>
                        <span class="dlt-ar-value">{{ autoRefreshLabel }}</span>
                        <svg class="dlt-caret" viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="6 9 12 15 18 9"></polyline></svg>
                    </button>

                    @if (autoRefreshMenuOpen) {
                        <div class="dlt-autorefresh-menu">
                            @for (o of autoRefreshOptions; track o.id) {
                                <button
                                    type="button"
                                    class="dlt-autorefresh-item"
                                    [class.dlt-autorefresh-item--active]="autoRefreshSeconds === o.id"
                                    (click)="setAutoRefresh(o.id)"
                                >
                                    <span>{{ o.label }}</span>
                                    @if (autoRefreshSeconds === o.id) {
                                        <svg class="dlt-ar-check" viewBox="0 0 16 16" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="3 8.5 6.5 12 13 4.5"></polyline></svg>
                                    }
                                </button>
                            }
                        </div>
                    }
                </div>

                <!-- Column show/hide dropdown -->
                <div class="dlt-columns" (click)="$event.stopPropagation()">
                    <button
                        type="button"
                        class="dlt-columns-btn"
                        [class.dlt-columns-btn--open]="columnsMenuOpen"
                        aria-label="Columns"
                        matTooltip="Columns"
                        matTooltipShowDelay="300"
                        (click)="toggleColumnsMenu($event)"
                    >
                        <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="3" y1="6" x2="21" y2="6"></line><line x1="3" y1="12" x2="21" y2="12"></line><line x1="3" y1="18" x2="21" y2="18"></line></svg>
                        <span class="dlt-columns-btn-label">Columns</span>
                        @if (hiddenColumnCount > 0) {
                            <span class="dlt-columns-badge">{{ hiddenColumnCount }}</span>
                        }
                        <svg class="dlt-caret" viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="6 9 12 15 18 9"></polyline></svg>
                    </button>

                    @if (columnsMenuOpen) {
                        <div class="dlt-columns-menu">
                            <div class="dlt-columns-menu-head">
                                <span class="dlt-columns-menu-title">Columns</span>
                                <span class="dlt-columns-menu-sub">Show or hide</span>
                            </div>
                            @for (c of columnOptions; track c.key) {
                                <button type="button" class="dlt-columns-item" (click)="toggleColumn(c.key)">
                                    <span class="dlt-columns-check" [class.dlt-columns-check--on]="c.visible">
                                        @if (c.visible) {
                                            <svg viewBox="0 0 16 16" width="11" height="11" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="3 8.5 6.5 12 13 4.5"></polyline></svg>
                                        }
                                    </span>
                                    <span class="dlt-columns-item-label">{{ c.label }}</span>
                                </button>
                            }

                            <!-- Expanded-detail columns (shown when a row is opened) -->
                            <div class="dlt-columns-section">Expanded detail</div>
                            @for (c of detailColumnOptions; track c.key) {
                                <button type="button" class="dlt-columns-item" (click)="toggleDetailColumn(c.key)">
                                    <span class="dlt-columns-check" [class.dlt-columns-check--on]="c.visible">
                                        @if (c.visible) {
                                            <svg viewBox="0 0 16 16" width="11" height="11" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="3 8.5 6.5 12 13 4.5"></polyline></svg>
                                        }
                                    </span>
                                    <span class="dlt-columns-item-label">{{ c.label }}</span>
                                </button>
                            }

                            <button type="button" class="dlt-columns-reset" (click)="resetColumns()">
                                <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"></path><path d="M3 3v5h5"></path></svg>
                                <span>Reset to default</span>
                            </button>
                        </div>
                    }
                </div>
            </div>
        </div>

        <div class="dlt-filters">
        <div class="dlt-search-row">
            <!-- Search + identity filters as labeled controls, mirroring the toolbar above. -->
            <div class="dlt-idcontrol dlt-idcontrol--search">
            <span class="dlt-control-label">Search</span>
            <div class="dlt-search">
                <svg class="dlt-search-icon" viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true">
                    <circle cx="7" cy="7" r="4.5"></circle>
                    <line x1="10.6" y1="10.6" x2="14.5" y2="14.5"></line>
                </svg>
                <input
                    type="text"
                    class="dlt-search-input"
                    placeholder="Service, identity, or address…"
                    [ngModel]="search"
                    (ngModelChange)="onSearchChange($event)"
                />
                @if (search) {
                    <button type="button" class="dlt-search-clear" (click)="onSearchChange('')" aria-label="Clear search">&times;</button>
                }
            </div>
            </div>

            <div class="dlt-idfilters">
            <div class="dlt-idcontrol dlt-idcontrol--filter">
            <span class="dlt-control-label">Dialing Identity</span>
            <div class="dlt-idfilter" (click)="$event.stopPropagation()">
                <button type="button" class="dlt-columns-btn" [class.dlt-columns-btn--open]="dialerMenuOpen" (click)="toggleDialerMenu($event)">
                    <span class="dlt-columns-btn-label" [class.dlt-columns-btn-label--placeholder]="dialerFilter.size === 0">{{ dialerFilter.size > 0 ? 'Selected' : 'All' }}</span>
                    @if (dialerFilter.size > 0) { <span class="dlt-columns-badge">{{ dialerFilter.size }}</span> }
                    <svg class="dlt-caret" viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="6 9 12 15 18 9"></polyline></svg>
                </button>
                @if (dialerMenuOpen) {
                    <div class="dlt-columns-menu dlt-idfilter-menu">
                        <input type="text" class="dlt-idfilter-search" placeholder="Search dialers…" [ngModel]="dialerSearch" (ngModelChange)="dialerSearch = $event" (click)="$event.stopPropagation()" />
                        <div class="dlt-idfilter-list">
                            @for (o of filteredDialerOptions(); track o.id) {
                                <button type="button" class="dlt-columns-item" (click)="toggleDialerFilter(o.id)">
                                    <span class="dlt-columns-check" [class.dlt-columns-check--on]="dialerFilter.has(o.id)">
                                        @if (dialerFilter.has(o.id)) {
                                            <svg viewBox="0 0 16 16" width="11" height="11" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="3 8.5 6.5 12 13 4.5"></polyline></svg>
                                        }
                                    </span>
                                    <span class="dlt-columns-item-label dlt-idfilter-name">{{ o.name }}</span>
                                </button>
                            } @empty {
                                <div class="dlt-idfilter-empty">No matches</div>
                            }
                        </div>
                        @if (dialerFilter.size > 0) {
                            <button type="button" class="dlt-columns-reset" (click)="clearDialerFilter()">
                                <span>Clear ({{ dialerFilter.size }})</span>
                            </button>
                        }
                    </div>
                }
            </div>
            </div>

            <div class="dlt-idcontrol dlt-idcontrol--filter">
            <span class="dlt-control-label">Binding Identity</span>
            <div class="dlt-idfilter" (click)="$event.stopPropagation()">
                <button type="button" class="dlt-columns-btn" [class.dlt-columns-btn--open]="binderMenuOpen" (click)="toggleBinderMenu($event)">
                    <span class="dlt-columns-btn-label" [class.dlt-columns-btn-label--placeholder]="binderFilter.size === 0">{{ binderFilter.size > 0 ? 'Selected' : 'All' }}</span>
                    @if (binderFilter.size > 0) { <span class="dlt-columns-badge">{{ binderFilter.size }}</span> }
                    <svg class="dlt-caret" viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="6 9 12 15 18 9"></polyline></svg>
                </button>
                @if (binderMenuOpen) {
                    <div class="dlt-columns-menu dlt-idfilter-menu">
                        <input type="text" class="dlt-idfilter-search" placeholder="Search binders…" [ngModel]="binderSearch" (ngModelChange)="binderSearch = $event" (click)="$event.stopPropagation()" />
                        <div class="dlt-idfilter-list">
                            @for (o of filteredBinderOptions(); track o.id) {
                                <button type="button" class="dlt-columns-item" (click)="toggleBinderFilter(o.id)">
                                    <span class="dlt-columns-check" [class.dlt-columns-check--on]="binderFilter.has(o.id)">
                                        @if (binderFilter.has(o.id)) {
                                            <svg viewBox="0 0 16 16" width="11" height="11" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="3 8.5 6.5 12 13 4.5"></polyline></svg>
                                        }
                                    </span>
                                    <span class="dlt-columns-item-label dlt-idfilter-name">{{ o.name }}</span>
                                </button>
                            } @empty {
                                <div class="dlt-idfilter-empty">No matches</div>
                            }
                        </div>
                        @if (binderFilter.size > 0) {
                            <button type="button" class="dlt-columns-reset" (click)="clearBinderFilter()">
                                <span>Clear ({{ binderFilter.size }})</span>
                            </button>
                        }
                    </div>
                }
            </div>
            </div>
            </div>
        </div>
        </div>
    </div>

    <!-- Results: the table, with the load bar pinned to its top edge (over the results, where the
         reloading data is) rather than the panel top. -->
    <div class="dlt-results">
        @if (isLoading) {
            <div class="dlt-loadbar" aria-hidden="true"></div>
        }
        <div class="dlt-table-area">
        @if (isLoading && groups.length === 0) {
            <table class="dlt-table dlt-table--skeleton" aria-hidden="true">
                <colgroup>
                    <col class="dlt-col-name" />
                    <col class="dlt-col-counterpart" />
                    <col class="dlt-col-addr" />
                    <col class="dlt-col-dials" />
                    <col class="dlt-col-time" />
                </colgroup>
                <thead>
                    <tr>
                        <th class="dlt-th"><span class="dlt-th-label">{{ primaryColumnLabel }}</span></th>
                        <th class="dlt-th"><span class="dlt-th-label">{{ counterpartLabel }}</span></th>
                        <th class="dlt-th"><span class="dlt-th-label">Dialed Address</span></th>
                        <th class="dlt-th"><span class="dlt-th-label">Dials</span></th>
                        <th class="dlt-th dlt-th--right"><span class="dlt-th-label">Last Dial</span></th>
                    </tr>
                </thead>
                <tbody>
                    @for (row of skeletonRows; track $index) {
                        <tr class="dlt-skel-row">
                            <td class="dlt-td"><span class="dlt-skel" [style.width.%]="55"></span></td>
                            <td class="dlt-td"><span class="dlt-skel" [style.width.%]="65"></span></td>
                            <td class="dlt-td"><span class="dlt-skel" [style.width.%]="50"></span></td>
                            <td class="dlt-td"><span class="dlt-skel" [style.width.%]="70"></span></td>
                            <td class="dlt-td dlt-td--right"><span class="dlt-skel" [style.width.%]="40"></span></td>
                        </tr>
                    }
                </tbody>
            </table>
        } @else if (groups.length === 0) {
            <div class="dlt-state">
                <svg class="dlt-state-icon" viewBox="0 0 16 16" width="30" height="30" fill="none" stroke="currentColor" stroke-width="1.3" aria-hidden="true">
                    <circle cx="7" cy="7" r="4.5"></circle>
                    <line x1="10.6" y1="10.6" x2="14.5" y2="14.5"></line>
                </svg>
                <p class="dlt-state-title">No dial activity in this range</p>
                <p class="dlt-state-subtitle">{{ emptyStateSubtitle }}</p>
                @if (hasMoreDials) {
                    <button type="button" class="dlt-coverage-btn dlt-state-loadmore" [disabled]="loadingMore" (click)="loadOlder()">
                        @if (loadingMore) {
                            <span class="pi pi-spinner pi-spin" aria-hidden="true"></span>Loading dials…
                        } @else {
                            <span class="pi pi-history" aria-hidden="true"></span>{{ loadMoreLabel }}
                        }
                    </button>
                }
            </div>
        } @else {
            <table class="dlt-table">
                <colgroup>
                    <col [style.width.%]="colWidths[0]" />
                    @if (colVisible['counterpart']) { <col [style.width.%]="colWidths[1]" /> }
                    @if (colVisible['addr']) { <col [style.width.%]="colWidths[2]" /> }
                    @if (colVisible['dials']) { <col [style.width.%]="colWidths[3]" /> }
                    @if (colVisible['time']) { <col [style.width.%]="colWidths[4]" /> }
                </colgroup>
                <thead>
                    <tr>
                        <th class="dlt-th" [class.dlt-th--sorted]="groupSortCol === 'name'" (click)="setGroupSort('name')">
                            <span class="dlt-th-label">{{ primaryColumnLabel }}</span>
                            @if (groupSortCol === 'name') {
                                <i class="dlt-sort" [class.dlt-sort--desc]="groupSortDir === 'desc'" aria-hidden="true"></i>
                            }
                            <span class="dlt-th-resize" (mousedown)="startColResize($event, 0)" (click)="$event.stopPropagation()"></span>
                        </th>
                        @if (colVisible['counterpart']) {
                            <th class="dlt-th dlt-th--static">
                                <span class="dlt-th-label">{{ counterpartLabel }}</span>
                                <span class="dlt-th-resize" (mousedown)="startColResize($event, 1)" (click)="$event.stopPropagation()"></span>
                            </th>
                        }
                        @if (colVisible['addr']) {
                            <th class="dlt-th" [class.dlt-th--sorted]="groupSortCol === 'listen'" (click)="setGroupSort('listen')">
                                <span class="dlt-th-label">Dialed Address</span>
                                @if (groupSortCol === 'listen') {
                                    <i class="dlt-sort" [class.dlt-sort--desc]="groupSortDir === 'desc'" aria-hidden="true"></i>
                                }
                                <span class="dlt-th-resize" (mousedown)="startColResize($event, 2)" (click)="$event.stopPropagation()"></span>
                            </th>
                        }
                        @if (colVisible['dials']) {
                            <th class="dlt-th" [class.dlt-th--sorted]="groupSortCol === 'status'" (click)="setGroupSort('status')">
                                <span class="dlt-th-label">Dials</span>
                                @if (groupSortCol === 'status') {
                                    <i class="dlt-sort" [class.dlt-sort--desc]="groupSortDir === 'desc'" aria-hidden="true"></i>
                                }
                                <span class="dlt-th-resize" (mousedown)="startColResize($event, 3)" (click)="$event.stopPropagation()"></span>
                            </th>
                        }
                        @if (colVisible['time']) {
                            <th class="dlt-th dlt-th--right" [class.dlt-th--sorted]="groupSortCol === 'lastDial'" (click)="setGroupSort('lastDial')">
                                <span class="dlt-th-label">Last Dial</span>
                                @if (groupSortCol === 'lastDial') {
                                    <i class="dlt-sort" [class.dlt-sort--desc]="groupSortDir === 'desc'" aria-hidden="true"></i>
                                }
                            </th>
                        }
                    </tr>
                </thead>
                <tbody [class.dlt-tbody--loading]="isLoading">
                    @for (g of visibleGroups; track g.key) {
                        <tr class="dlt-group-row" (click)="toggleGroup(g.key)">
                            <td class="dlt-td dlt-td--name">
                                <div class="dlt-name-cell">
                                    <i
                                        class="dlt-chevron"
                                        [class.dlt-chevron--open]="isExpanded(g.key)"
                                        aria-hidden="true"
                                    ></i>
                                    @if (g.primaryName) {
                                        @if (primaryLink(g); as link) {
                                            <a class="dlt-name dlt-link" [routerLink]="link" [matTooltip]="g.primaryName" (click)="onEntityLinkClick($event)">{{ g.primaryName }}</a>
                                        } @else {
                                            <span class="dlt-name" [matTooltip]="g.primaryName">{{ g.primaryName }}</span>
                                        }
                                    } @else {
                                        <span class="dlt-muted">—</span>
                                    }
                                </div>
                            </td>
                            @if (colVisible['counterpart']) {
                                <td class="dlt-td dlt-td--ellipsis" [matTooltip]="counterpartGroupLabel(g) ?? ''">
                                    @if (counterpartGroupLabel(g); as label) {
                                        @if (counterpartGroupLink(g); as link) {
                                            <a class="dlt-link" [routerLink]="link" (click)="onEntityLinkClick($event)">{{ label }}</a>
                                        } @else {
                                            {{ label }}
                                        }
                                    } @else {
                                        <span class="dlt-muted">—</span>
                                    }
                                </td>
                            }
                            @if (colVisible['addr']) {
                                <td class="dlt-td dlt-td--ellipsis">
                                    @if (g.interceptAddress || g.listenAddress; as addr) {
                                        <span
                                            class="dlt-addr"
                                            [class.dlt-addr--fallback]="!g.interceptAddress"
                                            [matTooltip]="g.interceptAddress ? 'Intercept address (what clients dial)' : 'Forward address (host terminator)'"
                                        >{{ addr }}</span>
                                    } @else {
                                        <span class="dlt-muted">—</span>
                                    }
                                </td>
                            }
                            @if (colVisible['dials']) {
                                <td class="dlt-td">
                                    @if (g.failedCount === 0) {
                                        <div
                                            class="dlt-meter dlt-meter--clean"
                                            [matTooltip]="g.count + ' dial' + (g.count === 1 ? '' : 's') + ', none failed'"
                                        >
                                            <svg class="dlt-meter-check" viewBox="0 0 16 16" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                                                <polyline points="3 8.5 6.5 12 13 4.5"></polyline>
                                            </svg>
                                            <span>{{ g.count }}<span class="dlt-meter-word"> successful</span></span>
                                        </div>
                                    } @else {
                                        <div class="dlt-meter" [matTooltip]="g.failedCount + ' of ' + g.count + ' dials failed'">
                                            <div class="dlt-meter-counts">
                                                <span class="dlt-meter-failed">{{ g.failedCount }}</span>
                                                <span class="dlt-meter-divider">/</span>
                                                <span class="dlt-meter-total">{{ g.count }}</span>
                                                <span class="dlt-meter-label">failed</span>
                                            </div>
                                            <div class="dlt-meter-bar">
                                                <div class="dlt-meter-fill" [style.width.%]="(g.failedCount / g.count) * 100"></div>
                                            </div>
                                        </div>
                                    }
                                </td>
                            }
                            @if (colVisible['time']) {
                                <td class="dlt-td dlt-td--right dlt-td--nowrap">
                                    <span [matTooltip]="formatTimestamp(g.latestTimestampMs)" matTooltipPosition="left">
                                        {{ formatTimestampRelative(g.latestTimestampMs) }}
                                    </span>
                                </td>
                            }
                        </tr>

                        @if (isExpanded(g.key)) {
                            <!-- Expanded detail lives in its own table, so hiding parent columns never affects it. -->
                            <tr class="dlt-detail-row">
                                <td class="dlt-detail-cell" [attr.colspan]="visibleColCount">
                                    <!-- The outcome chip, shared by bucket summaries and individual dial rows. -->
                                    <ng-template #outcomeChip let-r="r">
                                        @if (r.success === true) {
                                            <span class="dlt-chip dlt-chip--success">
                                                <svg viewBox="0 0 16 16" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><polyline points="3 8.5 6.5 12 13 4.5"></polyline></svg>
                                                <span>Success</span>
                                            </span>
                                        } @else if (r.success === false) {
                                            <span class="dlt-chip dlt-chip--failure">
                                                <svg viewBox="0 0 16 16" width="11" height="11" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><line x1="4" y1="4" x2="12" y2="12"></line><line x1="12" y1="4" x2="4" y2="12"></line></svg>
                                                <span>Failed</span>
                                            </span>
                                        } @else if (r.eventType === 'pathUpdated') {
                                            <span class="dlt-chip dlt-chip--state">
                                                <svg viewBox="0 0 16 16" width="12" height="12" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><polyline points="2 5 12 5"></polyline><polyline points="10 3 12.5 5 10 7"></polyline><polyline points="14 11 4 11"></polyline><polyline points="6 9 3.5 11 6 13"></polyline></svg>
                                                <span>Re-routed</span>
                                            </span>
                                        } @else if (r.eventType === 'deleted') {
                                            <span class="dlt-chip dlt-chip--neutral">
                                                <svg viewBox="0 0 16 16" width="11" height="11" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><line x1="4" y1="4" x2="12" y2="12"></line><line x1="12" y1="4" x2="4" y2="12"></line></svg>
                                                <span>Closed</span>
                                            </span>
                                        } @else {
                                            <span class="dlt-chip dlt-chip--neutral">{{ r.eventType }}</span>
                                        }
                                    </ng-template>

                                    <!-- One dial row — a singleton bucket, or a dial revealed under an expanded bucket (nested). -->
                                    <ng-template #dialRow let-r="r" let-nested="nested">
                                        <tr
                                            class="dlt-child-row"
                                            [class.dlt-child-row--failed]="r.success === false"
                                            [class.dlt-child-row--clickable]="r.success === false"
                                            [attr.title]="r.success === false ? 'View failure detail' : null"
                                            (click)="onChildRowClick(r)"
                                        >
                                            <td class="dlt-td dlt-td--child dlt-td--event" [class.dlt-td--nested]="nested" [class.dlt-td--dial]="!nested">
                                                <ng-container *ngTemplateOutlet="outcomeChip; context: { r: r }"></ng-container>
                                                @if (r.success === false) {
                                                    <span class="dlt-detail-cue" aria-hidden="true">
                                                        <svg viewBox="0 0 16 16" width="12" height="12" fill="none" stroke="currentColor" stroke-width="1.7"><polyline points="6 3.5 10.5 8 6 12.5"></polyline></svg>
                                                    </span>
                                                }
                                            </td>
                                            @if (detailColVisible['target']) {
                                                <!-- Target address (the terminator the dial hit) — present on success, often empty on failure. -->
                                                <td class="dlt-td dlt-td--child dlt-td--mono dlt-td--ellipsis" [matTooltip]="r.terminatorRemote ?? ''">
                                                    {{ r.terminatorRemote || '—' }}
                                                </td>
                                            }
                                            @if (detailColVisible['error']) {
                                                <td class="dlt-td dlt-td--child" [matTooltip]="failureTooltip(r)" matTooltipPosition="below">
                                                    @if (r.failureCause) {
                                                        <span class="dlt-code">{{ r.failureCause }}</span>
                                                    } @else {
                                                        <span class="dlt-muted">—</span>
                                                    }
                                                </td>
                                            }
                                            <td class="dlt-td dlt-td--child"></td>
                                            @if (detailColVisible['time']) {
                                                <td class="dlt-td dlt-td--child dlt-td--right dlt-td--nowrap">
                                                    <span [matTooltip]="formatTimestamp(r.timestampMs)" matTooltipPosition="left">
                                                        {{ formatTimestampRelative(r.timestampMs) }}
                                                    </span>
                                                </td>
                                            }
                                        </tr>
                                    </ng-template>

                                    <ng-template #dialTable let-records="records" let-key="key">
                                    <div class="dlt-card">
                                    <table class="dlt-detail-table">
                                        <colgroup>
                                            <col [style.width.%]="detailColWidths['event']" />
                                            @if (detailColVisible['target']) { <col [style.width.%]="detailColWidths['target']" /> }
                                            @if (detailColVisible['error']) { <col [style.width.%]="detailColWidths['error']" /> }
                                            <col [style.width.%]="detailColWidths['dials']" />
                                            @if (detailColVisible['time']) { <col [style.width.%]="detailColWidths['time']" /> }
                                        </colgroup>
                                        <thead>
                                            <tr class="dlt-subheader-row">
                                                <th class="dlt-sub-th dlt-sub-th--indent">Event
                                                    <span class="dlt-th-resize" (mousedown)="startDetailColResize($event, 'event')" (click)="$event.stopPropagation()"></span>
                                                </th>
                                                @if (detailColVisible['target']) {
                                                    <th class="dlt-sub-th">Target Address
                                                        <span class="dlt-th-resize" (mousedown)="startDetailColResize($event, 'target')" (click)="$event.stopPropagation()"></span>
                                                    </th>
                                                }
                                                @if (detailColVisible['error']) {
                                                    <th class="dlt-sub-th">Error Code
                                                        <span class="dlt-th-resize" (mousedown)="startDetailColResize($event, 'error')" (click)="$event.stopPropagation()"></span>
                                                    </th>
                                                }
                                                <th class="dlt-sub-th dlt-sub-th--right">Dials
                                                    <span class="dlt-th-resize" (mousedown)="startDetailColResize($event, 'dials')" (click)="$event.stopPropagation()"></span>
                                                </th>
                                                @if (detailColVisible['time']) { <th class="dlt-sub-th dlt-sub-th--right">Time</th> }
                                            </tr>
                                        </thead>
                                        <tbody>
                            <!-- Roll the leaf's dials into outcome buckets so identical rows collapse;
                                 a singleton bucket renders as the dial itself, larger ones expand. -->
                            @for (sig of signatureGroups(records); track sig.key) {
                                @if (sig.count === 1) {
                                    <ng-container *ngTemplateOutlet="dialRow; context: { r: sig.records[0] }"></ng-container>
                                } @else {
                                    @let sigKey = signatureKey(key, sig.key);
                                    <tr
                                        class="dlt-bucket-row"
                                        [class.dlt-bucket-row--failed]="sig.success === false"
                                        [class.dlt-bucket-row--open]="isExpanded(sigKey)"
                                        (click)="toggleGroup(sigKey)"
                                    >
                                        <td class="dlt-td dlt-td--child dlt-td--event dlt-td--bucket">
                                            <i class="dlt-chevron" [class.dlt-chevron--open]="isExpanded(sigKey)" aria-hidden="true"></i>
                                            <ng-container *ngTemplateOutlet="outcomeChip; context: { r: sig.records[0] }"></ng-container>
                                        </td>
                                        @if (detailColVisible['target']) {
                                            <td class="dlt-td dlt-td--child dlt-td--mono dlt-td--ellipsis" [matTooltip]="sig.targetAddress ?? ''">
                                                {{ sig.targetAddress || '—' }}
                                            </td>
                                        }
                                        @if (detailColVisible['error']) {
                                            <td class="dlt-td dlt-td--child">
                                                @if (sig.errorCode) {
                                                    <span class="dlt-code">{{ sig.errorCode }}</span>
                                                } @else {
                                                    <span class="dlt-muted">—</span>
                                                }
                                            </td>
                                        }
                                        <td class="dlt-td dlt-td--child dlt-td--right dlt-td--nowrap dlt-td--count">{{ sig.count | number }}</td>
                                        @if (detailColVisible['time']) {
                                            <td class="dlt-td dlt-td--child dlt-td--right dlt-td--nowrap">
                                                <span [matTooltip]="formatTimestamp(sig.latestTimestampMs)" matTooltipPosition="left">
                                                    {{ formatTimestampRelative(sig.latestTimestampMs) }}
                                                </span>
                                            </td>
                                        }
                                    </tr>
                                    @if (isExpanded(sigKey)) {
                                        @for (r of visibleChildrenOf(sig.records, sigKey); track r.itemIndex) {
                                            <ng-container *ngTemplateOutlet="dialRow; context: { r: r, nested: true }"></ng-container>
                                        }
                                        @if (sig.records.length > childLimit(sigKey)) {
                                            <tr class="dlt-child-row">
                                                <td class="dlt-td dlt-td--child dlt-td--indent" [attr.colspan]="visibleDetailColCount">
                                                    <button type="button" class="dlt-more-link" (click)="showMoreChildren(sigKey); $event.stopPropagation()">
                                                        <i class="dlt-chevron dlt-chevron--open" aria-hidden="true"></i>
                                                        Show {{ CHILD_PAGE_SIZE }} more · {{ sig.records.length - childLimit(sigKey) }} remaining
                                                    </button>
                                                </td>
                                            </tr>
                                        }
                                    }
                                }
                            }
                                        </tbody>
                                    </table>
                                    </div>
                                    </ng-template>
                                    @if (showBinderGroups(g)) {
                                        <!-- Multiple binders → a containerized card of expandable sub-groups. -->
                                        @let subs = binderSubgroups(g);
                                        <div class="dlt-nest">
                                            <div class="dlt-card">
                                                <div class="dlt-binder-header" [style.grid-template-columns]="binderGridTemplate">
                                                    <div class="dlt-binder-hcell">{{ counterpartLabel }}</div>
                                                    @if (colVisible['addr']) { <div class="dlt-binder-hcell"></div> }
                                                    @if (colVisible['dials']) { <div class="dlt-binder-hcell">Dials</div> }
                                                    @if (colVisible['time']) { <div class="dlt-binder-hcell dlt-binder-hcell--right">Last Dial</div> }
                                                </div>
                                                @for (b of subs; track b.id) {
                                                    <div class="dlt-binder">
                                                        <div
                                                            class="dlt-binder-head"
                                                            [class.dlt-binder-head--open]="isBinderExpanded(g.key, b.id)"
                                                            [style.grid-template-columns]="binderGridTemplate"
                                                            (click)="toggleBinder(g.key, b.id)"
                                                        >
                                                            <div class="dlt-binder-lead">
                                                                <i class="dlt-chevron" [class.dlt-chevron--open]="isBinderExpanded(g.key, b.id)" aria-hidden="true"></i>
                                                                @if (b.link) {
                                                                    <a class="dlt-link dlt-binder-name" [routerLink]="b.link" [matTooltip]="b.name ?? ''" (click)="onEntityLinkClick($event)">{{ b.name ?? '—' }}</a>
                                                                } @else {
                                                                    <span class="dlt-binder-name" [matTooltip]="b.name ?? ''">{{ b.name ?? '—' }}</span>
                                                                }
                                                            </div>
                                                            @if (colVisible['addr']) { <div class="dlt-binder-cell"></div> }
                                                            @if (colVisible['dials']) {
                                                                <div class="dlt-binder-cell">
                                                                    @if (b.failedCount === 0) {
                                                                        <div class="dlt-meter dlt-meter--clean" [matTooltip]="b.count + ' dial' + (b.count === 1 ? '' : 's') + ', none failed'">
                                                                            <svg class="dlt-meter-check" viewBox="0 0 16 16" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                                                                                <polyline points="3 8.5 6.5 12 13 4.5"></polyline>
                                                                            </svg>
                                                                            <span>{{ b.count }}<span class="dlt-meter-word"> successful</span></span>
                                                                        </div>
                                                                    } @else {
                                                                        <div class="dlt-meter" [matTooltip]="b.failedCount + ' of ' + b.count + ' dials failed'">
                                                                            <div class="dlt-meter-counts">
                                                                                <span class="dlt-meter-failed">{{ b.failedCount }}</span>
                                                                                <span class="dlt-meter-divider">/</span>
                                                                                <span class="dlt-meter-total">{{ b.count }}</span>
                                                                                <span class="dlt-meter-label">failed</span>
                                                                            </div>
                                                                            <div class="dlt-meter-bar">
                                                                                <div class="dlt-meter-fill" [style.width.%]="(b.failedCount / b.count) * 100"></div>
                                                                            </div>
                                                                        </div>
                                                                    }
                                                                </div>
                                                            }
                                                            @if (colVisible['time']) {
                                                                <div class="dlt-binder-cell dlt-binder-cell--right">
                                                                    <span [matTooltip]="formatTimestamp(b.latestTimestampMs)" matTooltipPosition="left">
                                                                        {{ formatTimestampRelative(b.latestTimestampMs) }}
                                                                    </span>
                                                                </div>
                                                            }
                                                        </div>
                                                        @if (isBinderExpanded(g.key, b.id)) {
                                                            <div class="dlt-nest dlt-nest--inner">
                                                                <ng-container *ngTemplateOutlet="dialTable; context: { records: b.records, key: binderKey(g.key, b.id) }"></ng-container>
                                                            </div>
                                                        }
                                                    </div>
                                                }
                                            </div>
                                        </div>
                                    } @else {
                                        <div class="dlt-nest">
                                            <ng-container *ngTemplateOutlet="dialTable; context: { records: g.records, key: g.key }"></ng-container>
                                        </div>
                                    }
                                </td>
                            </tr>
                        }
                    }
                    @if (groups.length > groupLimit) {
                        <tr class="dlt-more-row">
                            <td class="dlt-td" [attr.colspan]="visibleColCount">
                                <button type="button" class="dlt-more-link dlt-more-link--center" (click)="showMoreGroups()">
                                    <i class="dlt-chevron dlt-chevron--open" aria-hidden="true"></i>
                                    Show {{ GROUP_PAGE_SIZE }} more · {{ groups.length - groupLimit }} remaining
                                </button>
                            </td>
                        </tr>
                    }
                </tbody>
            </table>
        }
        </div>
    </div>

</div>
